import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import Switch from '../ui/Switch';
import {
    deletePersonalContact, fetchLockedSummary, fetchMyContacts, fetchPersonalContactsStatus,
} from '../../lib/personalContacts';
import { buildPersonalExportCsv } from '../../lib/personalContactCsv';
import { downloadCsv } from '../../lib/contactCsv';
import { buildVcard, downloadVcard } from '../../lib/vcard';

// BL-177 PR D5/D7: Settings > My Contacts.
//   PLATFORM card (platform admins): My Contacts on/off and sharing on/off for the whole platform.
//   COMPANY card (company admins): the same two switches for this company - they can only make things STRICTER; when the platform has
//     a feature off, the company switch is grey and says so (the database ANDs the two, so a company can never loosen the platform).
//   SWITCHED OFF (everyone): the list is hidden and nothing can be created, edited or shared - but the owner can always EXPORT and DELETE
//     their own contacts, so those two are offered here.
//   LOCKED card (company admins): a COUNT per separated employee. Their My Contacts are part of their employment record: locked, kept,
//     and nobody (not the person, not admins, not platform admins) can open, export or move them. Reinstatement lifts the lock.
// When a feature is off the screens hide it and the database refuses the writes.

const card = 'rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4';

function Row({ title, hint, children }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--surface-border)] py-3 first:border-t-0">
            <div className="min-w-[12rem] flex-1">
                <div className="text-sm font-medium text-[var(--ink)]">{title}</div>
                {hint && <div className="text-xs text-[var(--ink-muted)]">{hint}</div>}
            </div>
            {children}
        </div>
    );
}

export default function MyContactsSettingsView({ userId, orgId, isOrgAdmin = false, isPlatformSuperAdmin = false, client = supabaseClient }) {
    const [status, setStatus] = useState(null);
    const [locked, setLocked] = useState([]);
    const [mine, setMine] = useState([]);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setStatus(await fetchPersonalContactsStatus(client));
        if (userId) { try { setMine(await fetchMyContacts(client, userId)); } catch { setMine([]); } }
        if (isOrgAdmin || isPlatformSuperAdmin) {
            try { setLocked(await fetchLockedSummary(client)); } catch { setLocked([]); }
        }
    }, [client, userId, isOrgAdmin, isPlatformSuperAdmin]);
    useEffect(() => { load(); }, [load]);

    const guard = async (action, done) => {
        setBusy(true); setError(null); setMessage(null);
        try { await action(); if (done) setMessage(done); await load(); } catch (err) { setError(err.message || String(err)); } finally { setBusy(false); }
    };

    const setPlatform = (patch) => guard(async () => {
        const { error: err } = await client.from('personal_contacts_platform_settings').update(patch).eq('singleton', true);
        if (err) throw err;
    }, 'Saved for the whole platform.');
    const setCompany = (patch) => guard(async () => {
        const { error: err } = await client.from('org_personal_contacts_settings').upsert([{ org_id: orgId, ...patch }], { onConflict: 'org_id' });
        if (err) throw err;
    }, 'Saved for this company.');

    if (!status) return <div className="p-6 text-[var(--ink-muted)]">Loading…</div>;
    const platform = status.platform || { my_contacts_enabled: true, sharing_enabled: true };
    const company = status.company || { my_contacts_enabled: true, sharing_enabled: true };

    return (
        <div className="mx-auto max-w-3xl space-y-4" data-testid="my-contacts-settings">
            <h2 className="text-2xl font-bold text-[var(--ink)]">My Contacts</h2>
            <p className="text-sm text-[var(--ink-muted)]">A private address book for each person. Only its owner can see it - not company admins, not platform admins - unless the owner shares it.</p>

            {!status.enabled && (
                <section className={card} aria-label="My contacts while switched off" data-testid="my-contacts-off">
                    <div className="text-sm font-semibold text-[var(--ink)]">My Contacts is switched off</div>
                    <p className="text-xs text-[var(--ink-muted)]">Your list is hidden and nothing can be added, edited or shared. You can still export or delete your own contacts{mine.length ? ` (${mine.length} saved)` : ''}.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" disabled={!mine.length} className="rounded-lg border border-[var(--surface-border)] px-3 py-1.5 text-sm font-medium hover:border-brand-500 disabled:opacity-40"
                            onClick={() => downloadCsv(`my-contacts-${new Date().toISOString().slice(0, 10)}.csv`, buildPersonalExportCsv(mine))}>Export CSV</button>
                        <button type="button" disabled={!mine.length} className="rounded-lg border border-[var(--surface-border)] px-3 py-1.5 text-sm font-medium hover:border-brand-500 disabled:opacity-40"
                            onClick={() => downloadVcard(`my-contacts-${new Date().toISOString().slice(0, 10)}.vcf`, buildVcard(mine))}>Export vCard</button>
                        <button type="button" disabled={!mine.length || busy} className="rounded-lg border border-[var(--surface-border)] px-3 py-1.5 text-sm font-medium hover:border-brand-500 disabled:opacity-40"
                            onClick={() => { if (window.confirm('Delete all of your My Contacts? This cannot be undone.')) guard(async () => { for (const contact of mine) await deletePersonalContact(client, contact.id); }, 'Your contacts were deleted.'); }}>
                            Delete all my contacts
                        </button>
                    </div>
                </section>
            )}

            {(isOrgAdmin || isPlatformSuperAdmin) && (
                <section className={card} aria-label="Company setting">
                    <div className="text-sm font-semibold text-[var(--ink)]">For this company</div>
                    <p className="text-xs text-[var(--ink-muted)]">A company can only be stricter than the platform. Switching a feature off hides it for everyone here and the database refuses new writes.</p>
                    <Row title="My Contacts" hint={platform.my_contacts_enabled ? 'On or off for everyone at this company.' : 'Turned off for the whole platform.'}>
                        <Switch checked={status.enabled} disabled={busy || !platform.my_contacts_enabled} label="My Contacts for this company"
                            onChange={(next) => setCompany({ my_contacts_enabled: next })} />
                    </Row>
                    <Row title="Sharing My Contacts with colleagues" hint={!platform.sharing_enabled ? 'Turned off for the whole platform.' : (!company.my_contacts_enabled ? 'My Contacts is off for this company.' : 'People can share a contact or their list with colleagues.')}>
                        <Switch checked={status.sharing_enabled} disabled={busy || !platform.sharing_enabled || !status.enabled} label="Sharing for this company"
                            onChange={(next) => setCompany({ sharing_enabled: next })} />
                    </Row>
                </section>
            )}

            {isPlatformSuperAdmin && (
                <section className={card} aria-label="Platform setting">
                    <div className="text-sm font-semibold text-[var(--ink)]">For the whole platform</div>
                    <Row title="My Contacts" hint="Off = no company can use My Contacts.">
                        <Switch checked={platform.my_contacts_enabled} disabled={busy} label="My Contacts on the platform" onChange={(next) => setPlatform({ my_contacts_enabled: next })} />
                    </Row>
                    <Row title="Sharing My Contacts" hint="Off = nobody can share a contact or a list.">
                        <Switch checked={platform.sharing_enabled} disabled={busy} label="Sharing on the platform" onChange={(next) => setPlatform({ sharing_enabled: next })} />
                    </Row>
                </section>
            )}

            {(isOrgAdmin || isPlatformSuperAdmin) && locked.length > 0 && (
                <section className={card} aria-label="Locked - separated employees" data-testid="locked-list">
                    <div className="text-sm font-semibold text-[var(--ink)]">Locked — separated employees</div>
                    <p className="text-xs text-[var(--ink-muted)]">A separated employee's My Contacts are part of their employment record. They are kept and locked: nobody can open, export or move them, not even admins. You see only how many. They unlock if the person is reinstated.</p>
                    {locked.map(person => (
                        <Row key={person.ownerId} title={person.name} hint="Locked — separated employee">
                            <span className="text-sm font-semibold text-[var(--ink)]">{person.locked} contact{person.locked === 1 ? '' : 's'}</span>
                        </Row>
                    ))}
                </section>
            )}

            {message && <div role="status" className="text-sm text-[var(--ink)]">{message}</div>}
            {error && <div role="alert" className="text-sm text-[var(--ui-alert)]">{error}</div>}
        </div>
    );
}
